import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { environment } from 'src/environments/environment';
import packageJson from '../../../package.json';
import { CussService } from './cuss.service';
import { ErrorLoggerService } from './error-logger.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private sessionStart = Date.now();

  constructor(private injector: Injector) {}

  handleError(error: any): void {
    const errorToSend = error?.rejection || error?.originalError || error;

    try {
      const errorLogger = this.injector.get(ErrorLoggerService);
      errorLogger.logError(errorToSend, 'GlobalErrorHandler', {
        uiVersion: packageJson.version,
        errorComponent: this.extractComponent(errorToSend?.stack),
        sessionDuration: Math.round((Date.now() - this.sessionStart) / 1000),
        userAgent: navigator.userAgent,
        memoryUsed: this.getMemoryUsage(),
      });
    } catch {
      // Fallback: si ErrorLoggerService no está disponible, enviar directamente
      const newRelic = (window as any).newrelic;
      if (newRelic?.addPageAction) {
        const attrs = {
          name: 'KIOSK_SSBD',
          actionSubname: 'KIOSK_UNHANDLED_ERROR',
          errorSource: 'GlobalErrorHandler',
          errorType: errorToSend?.name || 'Unknown',
          errorMessage: (errorToSend?.message || String(errorToSend)).substring(0, 500),
          errorStack: (errorToSend?.stack || '').substring(0, 1000),
          kioskId: this.getKioskId(),
          pnr: this.getReservationCode(),
          currentRoute: window.location.hash || window.location.pathname,
          environment: environment.production ? 'production' : 'cert',
          isSSBD: true,
          isError: true,
        };
        newRelic.noticeError(errorToSend, attrs);
        newRelic.addPageAction('KIOSK_SSBD', attrs);
      }
    }

    console.error('[KioskError]', error);
  }

  private getKioskId(): string {
    try {
      const cussService = this.injector.get(CussService);
      return cussService?.KioskID || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  private getReservationCode(): string {
    try {
      const data = sessionStorage.getItem('reservation');
      if (data) {
        const reservation = JSON.parse(data);
        return reservation?.pnr || 'empty';
      }
    } catch {}
    return 'empty';
  }

  private extractComponent(stack: string): string {
    try {
      if (!stack) return 'unknown';
      const match = stack.match(/at (\w+Component)/);
      return match ? match[1] : 'unknown';
    } catch {}
    return 'unknown';
  }

  private getMemoryUsage(): string {
    try {
      const perf = performance as any;
      if (perf?.memory) {
        const usedMB = Math.round(perf.memory.usedJSHeapSize / 1048576);
        const totalMB = Math.round(perf.memory.totalJSHeapSize / 1048576);
        return `${usedMB}MB/${totalMB}MB`;
      }
    } catch {}
    return 'unavailable';
  }
}
